import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {getCompanyAdminList, getCompanyList} from '../../Management/core/_requests'
import type {Vessel} from '../../Management/core/_models'
import {fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels} from '../../../shared/access/accessibleVessels'
import type {
  QHSESafetyCommitteeMeetingDto,
  SafetyCommitteeCreatedByRole,
  SafetyCommitteeStatus,
  SafetyCommitteeFindingLinkType,
  SafetyCommitteeClosureResult,
} from '../core/_models'
import {
  searchSafetyCommitteeMeetings,
  createSafetyCommitteeMeeting,
  updateSafetyCommitteeMeeting,
  submitSafetyCommitteeMeeting,
  closeSafetyCommitteeMeeting,
  uploadSafetyCommitteeAttachments,
  getSafetyCommitteeMeeting,
  listSafetyCommitteeAttachments,
  safetyCommitteeAttachmentViewUrl,
  safetyCommitteeAttachmentDownloadUrl,
  type SafetyCommitteeAttachmentType,
} from '../core/_requests'
import {SCMFormModal} from './SCMFormModal'
import {SCMReviewModal} from './SCMReviewModal'
import {ViewSCMModal} from './ViewSCMModal'
import SCMAttachmentsModal from './SCMAttachmentsModal'
import {FileViewerModal} from './FileViewerModal'
import {RemarksCell, SCMRemarksModal} from './StructuredRemarksView'
import { QHSE_PERMS } from '../core/permissions'
import {
  filterVesselsForManagementList,
  getUserCompanyGroupAdminId,
  isBroadFleetPickerUser,
  isLikelyVesselAssignedUser,
} from '../core/qhseUserContext'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

type SCMStatus = SafetyCommitteeStatus

type AttachmentItem = {name: string}

type SCMRecord = {
  id: number
  scmName?: string
  dateHeld: string
  findingName: string
  findingsLinkType: SafetyCommitteeFindingLinkType | ''
  findingId?: number | null
  presentSafetyOfficer: string
  designatedSafetyOfficer: string
  mastersRemarks: string
  officeRemarks: string
  attachedForm: AttachmentItem[]
  checklistAttachments: AttachmentItem[]
  findingAttachments: AttachmentItem[]
  officeAttachments: AttachmentItem[]
  status: SCMStatus
  createdByRole?: SafetyCommitteeCreatedByRole | null
  submittedAt?: string
  officeReviewed?: boolean
  closureResult?: SafetyCommitteeClosureResult | null
  vesselId?: number | null
  companyGroupAdminId?: number | null
  companyAdminId?: number | null
}

type SCMAttachmentModalState = {
  visible: boolean
  loading: boolean
  title: string
  subtitle?: string | null
  items: {
    id: string
    fileName: string
    viewUrl: string
    downloadUrl: string
    type: SafetyCommitteeAttachmentType
    typeLabel: string
    sizeBytes?: number
    mimeType?: string | null
  }[]
}

type SCMFormState = Omit<
  SCMRecord,
  'id' | 'status' | 'createdByRole' | 'submittedAt' | 'officeReviewed' | 'companyGroupAdminId' | 'companyAdminId'
>

type SCMUploadFiles = {
  attachedForm: File[]
  checklistAttachments: File[]
  findingAttachments: File[]
  officeAttachments: File[]
}

const getStatusDisplay = (
  record: {
    status?: SCMStatus | null
    officeReviewed?: boolean
    closureResult?: SafetyCommitteeClosureResult | null
  },
  shipboardUser?: boolean
) => {
  const status = record?.status
  if (!status) {
    return {label: '-', badgeClass: 'badge-light'}
  }
  if (status === 'SAVE') {
    return {label: 'Saved', badgeClass: 'badge-light-secondary'}
  }
  if (status === 'SUBMIT') {
    if (shipboardUser) {
      return {label: 'Submitted', badgeClass: 'badge-light-warning'}
    }
    if (record?.officeReviewed) {
      return {label: 'Reviewed', badgeClass: 'badge-light-success'}
    }
    return {label: 'Ready for Review', badgeClass: 'badge-light-warning'}
  }
  if (status === 'CLOSE') {
    if (record?.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION') {
      return {label: 'Closed and TBR', badgeClass: 'badge-light-success'}
    }
    return {label: 'Closed Satisfactorily', badgeClass: 'badge-light-success'}
  }
  return {label: String(status), badgeClass: 'badge-light'}
}

const attachmentSummary = (items: AttachmentItem[] | undefined | null) => {
  if (!items || items.length === 0) return 0
  return items.length
}

const mapAttachmentNames = (names?: string[] | null): AttachmentItem[] => {
  if (!Array.isArray(names)) return []
  return names.map(name => ({name}))
}

const mapDtoToRecord = (dto: QHSESafetyCommitteeMeetingDto): SCMRecord => ({
  id: Number(dto.id),
  scmName: dto.scmName || '',
  dateHeld: dto.dateHeld || '',
  findingName: dto.findingsNumber || '',
  findingsLinkType: dto.meetingType || dto.findingsLinkType || '',
  findingId: dto.findingId ?? null,
  presentSafetyOfficer: dto.presentSafetyOfficer || '',
  designatedSafetyOfficer: dto.designatedSafetyOfficer || '',
  mastersRemarks: dto.mastersRemarks || '',
  officeRemarks: dto.officeRemarks || '',
  attachedForm: mapAttachmentNames(dto.attachedFormFiles),
  checklistAttachments: mapAttachmentNames(dto.checklistAttachments),
  findingAttachments: mapAttachmentNames(dto.findingAttachments),
  officeAttachments: mapAttachmentNames(dto.officeAttachments),
  status: (dto.status || 'SAVE') as SCMStatus,
  createdByRole: dto.createdByRole || null,
  submittedAt: dto.submittedAt || undefined,
  officeReviewed: dto.officeReviewed ?? false,
  closureResult: dto.closureResult ?? null,
  vesselId: dto.vesselId ?? null,
  companyGroupAdminId: null,
  companyAdminId: null,
})

const SafetyCommitteeMeetingPage: FC = () => {
  const {currentUser, auth, can, permissionsLoaded} = useAuth()

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser)

  const isTopLevel = broadFleet

  const effectiveCompanyId = isTopLevel
    ? undefined
    : (myCgaId ?? undefined)

  const canCreateScm = permissionsLoaded && can(QHSE_PERMS.SAFETY_COMMITTEE_CREATE)
  const canUpdateScm = permissionsLoaded && can(QHSE_PERMS.SAFETY_COMMITTEE_UPDATE)
  const canSubmitScm = permissionsLoaded && can(QHSE_PERMS.SAFETY_COMMITTEE_SUBMIT)
  const canCloseScm = permissionsLoaded && can(QHSE_PERMS.SAFETY_COMMITTEE_CLOSE)
  const canDeleteScm = permissionsLoaded && can(QHSE_PERMS.SAFETY_COMMITTEE_DELETE)
  const canReadScm = permissionsLoaded && can(QHSE_PERMS.SAFETY_COMMITTEE_READ)

  const canEditScmRemarks = canUpdateScm

  // Filters
  const [filters, setFilters] = useState<{
    companyId: string
    subcompanyId: string
    vesselId: string
    status: 'All' | SCMStatus
    meetingType: 'All' | SafetyCommitteeFindingLinkType
    meetingDate: string
  }>({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
    status: 'All',
    meetingType: 'All',
    meetingDate: '',
  })

  // Lookups
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])

  const [records, setRecords] = useState<SCMRecord[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [lookupsReady, setLookupsReady] = useState(false)
  const [didAutoSelectVessel, setDidAutoSelectVessel] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const fetchSeqRef = useRef<number>(0)
  const [addOpen, setAddOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [viewing, setViewing] = useState<QHSESafetyCommitteeMeetingDto | null>(null)
  const [editing, setEditing] = useState<SCMRecord | null>(null)
  const [submitId, setSubmitId] = useState<number | null>(null)
  const [reviewing, setReviewing] = useState<SCMRecord | null>(null)
  const [attachmentsModal, setAttachmentsModal] = useState<SCMAttachmentModalState>({
    visible: false,
    loading: false,
    title: 'Attachments',
    subtitle: null,
    items: [],
  })
  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })

  const openRemarksModal = (title: string, subtitle: string, text: string) => {
    setRemarksModal({
      visible: true,
      title,
      subtitle,
      text,
    })
  }

  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [sortColumn, setSortColumn] = useState<
    | 'dateHeld'
    | 'scmName'
    | 'findingName'
    | 'mastersRemarks'
    | 'officeRemarks'
    | 'status'
    | ''
  >('dateHeld')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.searchQuery !== undefined) {
      setSearchTerm(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vesselId: globalState.vessel?.id ? String(globalState.vessel.id) : '',
      companyId: globalState.company ? String(globalState.company.id) : '',
    }))
  }, [globalState.vessel, globalState.company])

  const loadMeetings = async () => {
    const seq = ++fetchSeqRef.current
    setLoading(true)
    try {
      if (shipboardUser && !currentUser?.vessel?.id) {
        if (isMountedRef.current && seq === fetchSeqRef.current) setRecords([])
        return
      }

      const exactDate = filters.meetingDate || undefined

      const params = {
        companyGroupId: shipboardUser
          ? undefined
          : (filters.companyId ? Number(filters.companyId) : effectiveCompanyId),
        companyId: shipboardUser ? undefined : (filters.subcompanyId ? Number(filters.subcompanyId) : undefined),
        vesselId: shipboardUser
          ? currentUser?.vessel?.id
          : (filters.vesselId ? Number(filters.vesselId) : undefined),
        status: filters.status === 'All' ? undefined : (filters.status as SCMStatus),
        fromDate: exactDate,
        toDate: exactDate,
      }

      const list = await searchSafetyCommitteeMeetings(params)
      if (!isMountedRef.current || seq !== fetchSeqRef.current) return
      setRecords(list.map(mapDtoToRecord))
    } catch (e) {
      console.error('Failed to load safety committee meetings', e)
      if (!isMountedRef.current || seq !== fetchSeqRef.current) return
      setRecords([])
      toast.error('Failed to load safety committee meetings', {position: 'top-center'})
    } finally {
      if (isMountedRef.current && seq === fetchSeqRef.current) {
        setLoading(false)
      }
    }
  }

  const openAllAttachmentsModal = async (record: SCMRecord) => {
    setAttachmentsModal({
      visible: true,
      loading: true,
      title: 'Attachments',
      subtitle: record.scmName ? `SCM: ${record.scmName}` : null,
      items: [],
    })

    try {
      const types: {type: SafetyCommitteeAttachmentType; label: string}[] = [
        {type: 'FORM', label: 'Form'},
        {type: 'CHECKLIST', label: 'Checklist'},
        {type: 'FINDING', label: 'Finding'},
        {type: 'OFFICE', label: 'Office'},
      ]

      const lists = await Promise.all(
        types.map(async ({type}) => {
          const list = await listSafetyCommitteeAttachments(record.id, type)
          return {type, list}
        })
      )

      const items = lists.flatMap(({type, list}) => {
        const typeLabel = types.find((t) => t.type === type)?.label ?? String(type)
        return list.map((it) => ({
          id: `${type}:${it.fileName}`,
          fileName: it.fileName,
          viewUrl: safetyCommitteeAttachmentViewUrl(record.id, type, it.fileName),
          downloadUrl: safetyCommitteeAttachmentDownloadUrl(record.id, type, it.fileName),
          type,
          typeLabel,
          sizeBytes: it.sizeBytes,
          mimeType: it.mimeType,
        }))
      })

      setAttachmentsModal({
        visible: true,
        loading: false,
        title: 'Attachments',
        subtitle: record.scmName ? `SCM: ${record.scmName}` : null,
        items,
      })
    } catch (e) {
      console.error('Failed to load attachments', e)
      toast.error('Failed to load attachments', {position: 'top-center'})
      setAttachmentsModal((prev) => ({...prev, loading: false}))
    }
  }

  const openFileViewer = (item: {fileName: string; viewUrl: string; downloadUrl: string}) => {
    setFileModal({
      visible: true,
      title: item.fileName,
      fileName: item.fileName,
      viewUrl: item.viewUrl,
      downloadUrl: item.downloadUrl,
    })
  }

  // Load lookups (Company, Subcompany, Vessel)
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [groupsRaw, adminsRaw, vesselList] = await Promise.all([
          getCompanyAdminList?.().catch(() => []),
          getCompanyList?.().catch(() => []),
          fetchAccessibleVesselsForCurrentUser().then(normalizeAccessibleVessels).catch(() => [] as Vessel[]),
        ])

        const groups: CompanyGroup[] = Array.isArray(groupsRaw)
          ? groupsRaw.map((g: any) => ({
              id: Number(g.id),
              name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
            }))
          : []

        const admins: Subcompany[] = Array.isArray(adminsRaw)
          ? adminsRaw.map((a: any) => ({
              id: Number(a.id),
              name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
              companyId: Number(
                a.cgaid?.id ??
                a.cga?.id ??
                a.companyGroupAdminId ??
                a.companyGroupId
              ),
            }))
          : []

        if (!isMountedRef.current) return
        setCompanies(groups.filter(g => Number.isFinite(g.id)))
        setSubcompanies(admins.filter(a => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

        let vesselsForUser = filterVesselsForManagementList(vesselList, currentUser)

        if (!isMountedRef.current) return
        setVesselsScoped(vesselsForUser)
      } catch (e) {
        console.error('Failed to load lookups', e)
        if (!isMountedRef.current) return
        setCompanies([])
        setSubcompanies([])
        setVesselsScoped([])
      } finally {
        if (!isMountedRef.current) return
        setLookupsReady(true)
      }
    }

    loadLookups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  useEffect(() => {
    loadMeetings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.companyId,
    filters.subcompanyId,
    filters.vesselId,
    filters.status,
    filters.meetingDate,
    effectiveCompanyId,
    shipboardUser,
    currentUser?.vessel?.id,
  ])

  // Derived: vessels filtered by Company / Subcompany filters
  const vesselsForFilters = useMemo(() => {
    let list = vesselsScoped
    if (filters.companyId) {
      const cid = Number(filters.companyId)
      list = list.filter((v: any) =>
        Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid
      )
    }
    if (filters.subcompanyId) {
      const scid = Number(filters.subcompanyId)
      list = list.filter((v: any) =>
        Number(v.companyAdmin?.id ?? v.companyId) === scid
      )
    }
    return list
  }, [vesselsScoped, filters.companyId, filters.subcompanyId])

  const subcompaniesForChosenCompany = useMemo(() => {
    if (!filters.companyId) return []
    const cid = Number(filters.companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [filters.companyId, subcompanies])

  const hasSubcompaniesForChosenCompany = subcompaniesForChosenCompany.length > 0

  // Auto-select vessel for shipboard users once lookups are ready (preserve 'All Vessels' for office users)
  useEffect(() => {
    if (!lookupsReady) return
    if (didAutoSelectVessel) return

    if (shipboardUser) {
      const userVesselId = currentUser?.vessel?.id
      if (userVesselId) {
        setFilters((prev) => ({
          ...prev,
          vesselId: String(userVesselId),
        }))
      }
    }
    setDidAutoSelectVessel(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsReady, shipboardUser, didAutoSelectVessel, currentUser])

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setFilters(prev => {
      const next = {...prev, [name]: value}
      if (name === 'companyId') {
        next.subcompanyId = ''
        next.vesselId = ''
      }
      if (name === 'subcompanyId') {
        next.vesselId = ''
      }
      return next
    })
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({
      companyId: '',
      subcompanyId: '',
      vesselId: '',
      status: 'All',
      meetingType: 'All',
      meetingDate: '',
    })
    setSearchTerm('')
    setCurrentPage(1)
  }

  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      const matchesSearch =
        (r.findingName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.scmName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.mastersRemarks || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.officeRemarks || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.presentSafetyOfficer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.designatedSafetyOfficer || '').toLowerCase().includes(searchTerm.toLowerCase())

      const byMeetingType =
        filters.meetingType === 'All' ||
        r.findingsLinkType === filters.meetingType

      const byMeetingDate =
        !filters.meetingDate ||
        (r.dateHeld ? r.dateHeld === filters.meetingDate : false)

      return matchesSearch && byMeetingType && byMeetingDate
    })
  }, [records, searchTerm, filters.meetingType, filters.meetingDate])

  const sortedRecords = useMemo(() => {
    const data = [...filteredRecords]
    if (!sortColumn) return data

    data.sort((a, b) => {
      if (sortColumn === 'dateHeld') {
        const at = a.dateHeld ? new Date(a.dateHeld).getTime() : 0
        const bt = b.dateHeld ? new Date(b.dateHeld).getTime() : 0
        const diff = at - bt
        return sortOrder === 'asc' ? diff : -diff
      }

      const aVal = (a as any)[sortColumn]
      const bVal = (b as any)[sortColumn]
      const aStr = (aVal ?? '').toString().toLowerCase()
      const bStr = (bVal ?? '').toString().toLowerCase()

      if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1
      if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return data
  }, [filteredRecords, sortColumn, sortOrder])

  const handleSort = (
    column:
      | 'dateHeld'
      | 'scmName'
      | 'findingName'
      | 'mastersRemarks'
      | 'officeRemarks'
      | 'status'
  ) => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortOrder(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortOrder('asc')
      return column
    })
  }

  // Pagination calculations
  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedRecords.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedRecords.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const uploadAttachments = async (id: number, uploads: SCMUploadFiles) => {
    const entries: Array<[SafetyCommitteeAttachmentType, File[]]> = [
      ['FORM', uploads.attachedForm],
      ['CHECKLIST', uploads.checklistAttachments],
      ['FINDING', uploads.findingAttachments],
      ['OFFICE', uploads.officeAttachments],
    ]

    for (const [type, files] of entries) {
      if (files.length > 0) {
        await uploadSafetyCommitteeAttachments(id, type, files)
      }
    }
  }

  const buildPayload = (form: SCMFormState, vesselId: number): Omit<
    QHSESafetyCommitteeMeetingDto,
    'id' | 'createdAt' | 'updatedAt' | 'status' | 'createdByRole' | 'submittedAt' | 'officeReviewed'
  > => ({
    vesselId,
    scmName: form.scmName || undefined,
    dateHeld: form.dateHeld || undefined,
    findingsNumber: form.findingName || undefined,
    meetingType: form.findingsLinkType || undefined,
    findingsLinkType: form.findingsLinkType || undefined,
    findingId: form.findingsLinkType === 'MONTHLY' ? undefined : (form.findingId ?? undefined),
    presentSafetyOfficer: form.presentSafetyOfficer || undefined,
    designatedSafetyOfficer: form.designatedSafetyOfficer || undefined,
    mastersRemarks: form.mastersRemarks || undefined,
    officeRemarks: form.officeRemarks || undefined,
  })

  const resolveVesselId = (form?: SCMFormState) => {
    if (form?.vesselId) return Number(form.vesselId)
    return shipboardUser ? currentUser?.vessel?.id : (filters.vesselId ? Number(filters.vesselId) : undefined)
  }

  const handleAddSave = async (form: SCMFormState, uploads: SCMUploadFiles) => {
    try {
      const vesselId = resolveVesselId(form)
      if (!vesselId) {
        toast.error('Please select a vessel', {position: 'top-center'})
        return
      }

      const created = await createSafetyCommitteeMeeting(buildPayload(form, vesselId))
      if (created.id) {
        await uploadAttachments(Number(created.id), uploads)
      }

      setAddOpen(false)
      setCurrentPage(1)
      toast.success('Safety committee meeting saved', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Failed to save safety committee meeting', e)
      toast.error(
        e?.response?.data?.message || 'Failed to save safety committee meeting',
        {position: 'top-center'}
      )
    }
  }

  const handleAddSubmit = async (form: SCMFormState, uploads: SCMUploadFiles) => {
    try {
      const vesselId = resolveVesselId(form)
      if (!vesselId) {
        toast.error('Please select a vessel', {position: 'top-center'})
        return
      }

      const created = await createSafetyCommitteeMeeting(buildPayload(form, vesselId))
      if (created.id) {
        const id = Number(created.id)
        await uploadAttachments(id, uploads)
        await submitSafetyCommitteeMeeting(id)
      }

      setAddOpen(false)
      setCurrentPage(1)
      toast.success('Safety committee meeting submitted', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Failed to submit safety committee meeting', e)
      toast.error(
        e?.response?.data?.message || 'Failed to submit safety committee meeting',
        {position: 'top-center'}
      )
    }
  }

  const handleEditSave = async (form: SCMFormState, uploads: SCMUploadFiles) => {
    if (!editing) return
    try {
      const vesselId = editing.vesselId ?? resolveVesselId(form)
      if (!vesselId) {
        toast.error('Please select a vessel', {position: 'top-center'})
        return
      }

      await updateSafetyCommitteeMeeting(editing.id, buildPayload(form, vesselId))
      await uploadAttachments(editing.id, uploads)

      setEditOpen(false)
      setEditing(null)
      toast.success('Safety committee meeting updated', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Failed to update safety committee meeting', e)
      toast.error(
        e?.response?.data?.message || 'Failed to update safety committee meeting',
        {position: 'top-center'}
      )
    }
  }

  const handleEditSubmit = async (form: SCMFormState, uploads: SCMUploadFiles) => {
    if (!editing) return
    try {
      const vesselId = editing.vesselId ?? resolveVesselId(form)
      if (!vesselId) {
        toast.error('Please select a vessel', {position: 'top-center'})
        return
      }

      await updateSafetyCommitteeMeeting(editing.id, buildPayload(form, vesselId))
      await uploadAttachments(editing.id, uploads)
      await submitSafetyCommitteeMeeting(editing.id)

      setEditOpen(false)
      setEditing(null)
      toast.success('Safety committee meeting submitted', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Failed to submit safety committee meeting', e)
      toast.error(
        e?.response?.data?.message || 'Failed to submit safety committee meeting',
        {position: 'top-center'}
      )
    }
  }

  const handleSubmitRow = async (id: number) => {
    try {
      await submitSafetyCommitteeMeeting(id)
      toast.success('Safety committee meeting submitted', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Submit safety committee meeting failed', e)
      toast.error(
        e?.response?.data?.message || 'Failed to submit safety committee meeting',
        {position: 'top-center'}
      )
    }
  }

  const handleSaveReview = async (officeRemarks: string, officeAttachments: File[]) => {
    if (!reviewing) return
    try {
      await updateSafetyCommitteeMeeting(reviewing.id, {
        officeRemarks,
        officeReviewed: true,
      })
      if (officeAttachments.length > 0) {
        await uploadSafetyCommitteeAttachments(reviewing.id, 'OFFICE', officeAttachments)
      }
      setReviewing(null)
      toast.success('Office review saved', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Office review failed', e)
      toast.error(
        e?.response?.data?.message || 'Failed to save office review',
        {position: 'top-center'}
      )
    }
  }

  const handleCloseWithResult = async (
    officeRemarks: string,
    officeAttachments: File[],
    result: SafetyCommitteeClosureResult
  ) => {
    if (!reviewing) return
    try {
      if (officeAttachments.length > 0) {
        await uploadSafetyCommitteeAttachments(reviewing.id, 'OFFICE', officeAttachments)
      }

      if (officeRemarks && officeRemarks.trim()) {
        await updateSafetyCommitteeMeeting(reviewing.id, {
          officeRemarks,
          officeReviewed: true,
        })
      }

      await closeSafetyCommitteeMeeting(reviewing.id, result)

      setReviewing(null)
      toast.success('Safety committee meeting closed', {position: 'top-center'})
      await loadMeetings()
    } catch (e: any) {
      console.error('Close safety committee meeting failed', e)
      toast.error(
        e?.response?.data?.message || 'Failed to close safety committee meeting',
        {position: 'top-center'}
      )
    }
  }

  // -----------------------
  // Modals: View / Edit
  // -----------------------

  const openView = async (id: number) => {
    try {
      const dto = await getSafetyCommitteeMeeting(id)
      setViewing(dto)
      setViewOpen(true)
    } catch (e) {
      console.error('Failed to load safety committee meeting', e)
      toast.error('Failed to load safety committee meeting', {position: 'top-center'})
    }
  }

  const openEdit = (rec: SCMRecord) => {
    setEditing(rec)
    setEditOpen(true)
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='card-label text-dark fw-bold mb-0'>Safety Committee Meeting</h3>
                {loading && (
                  <span className='badge badge-light-primary d-inline-flex align-items-center gap-2 py-2 px-3 fs-7 fw-normal'>
                    <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true'></span>
                    Loading meetings...
                  </span>
                )}
              </div>
              <div className='card-toolbar'>
                <button
                  type='button'
                  className='btn btn_primary'
                  disabled={!canCreateScm}
                  onClick={() => setAddOpen(true)}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                  Add SCM
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>
              </div>

              <div className='row gx-3 gy-3'>
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Search
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Findings, remarks, officers…'
                    value={searchTerm}
                    onChange={e => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1)
                    }}
                  />
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Status
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='status'
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value='All'>All</option>
                    <option value='SAVE'>Saved</option>
                    <option value='SUBMIT'>Submitted</option>
                    <option value='CLOSE'>Closed</option>
                  </SearchableSelect>
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Meeting Type
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='meetingType'
                    value={filters.meetingType}
                    onChange={handleFilterChange}
                  >
                    <option value='All'>All</option>
                    <option value='AUDIT'>Audit</option>
                    <option value='INSPECTION'>Incident</option>
                    <option value='MONTHLY'>Monthly</option>
                  </SearchableSelect>
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Meeting Date
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='meetingDate'
                    value={filters.meetingDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className='col-md-1 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn-light btn-sm w-100'
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <div className='report-table table-responsive'>
                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{minWidth: '70px'}}>
                          Sr/No
                        </th>
                        <th style={{minWidth: '180px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>SCM Name</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('scmName')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'scmName'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Date</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('dateHeld')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'dateHeld'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Finding Name</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('findingName')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'findingName'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '220px', maxWidth: '260px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Master&apos;s Remarks</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('mastersRemarks')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'mastersRemarks'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '220px', maxWidth: '260px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Office Remarks</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('officeRemarks')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'officeRemarks'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Attachments</span>
                          </div>
                        </th>
                        <th style={{minWidth: '120px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Status</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('status')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'status'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{minWidth: '170px', textAlign: 'center'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='table-body text-start'>
                      {loading ? (
                        Array.from({ length: 8 }).map((_, i) => (
                          <tr key={`scm-skeleton-${i}`}>
                            <td className='py-3 text-center'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-6 rounded' style={{ height: 16 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-9 rounded' style={{ height: 16 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-10 rounded' style={{ height: 16 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-6 rounded-pill' style={{ height: 18 }}></span>
                              </div>
                            </td>
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-7 rounded-pill' style={{ height: 18 }}></span>
                              </div>
                            </td>
                            <td className='py-3 text-center'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-8 rounded' style={{ height: 28 }}></span>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : currentRecords.length === 0 ? (
                        <tr>
                          <td colSpan={9} className='text-center text-muted py-5'>
                            No safety committee meetings found.
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((r, idx) => {
                          const createdByCrew = r.createdByRole === 'CREW'
                          const createdByOffice = r.createdByRole === 'OFFICE'
                          const isSaveLike = r.status === 'SAVE'

                          // If createdByRole is set, only the creator role can edit/submit
                          // If createdByRole is not set (legacy data), allow both roles to edit
                          const draftPartyOk =
                            isSaveLike &&
                            (
                              (createdByCrew && shipboardUser) ||
                              (createdByOffice && !shipboardUser) ||
                              (!createdByCrew && !createdByOffice)  // fallback for legacy records
                            )
                          const showScmEdit = draftPartyOk && canUpdateScm
                          const showScmSubmit = draftPartyOk && canSubmitScm

                          const canReview =
                            r.status === 'SUBMIT' && !shipboardUser && canUpdateScm

                          const meetingTypeLabel = r.findingsLinkType
                            ? r.findingsLinkType === 'AUDIT'
                              ? 'Audit'
                              : r.findingsLinkType === 'INSPECTION'
                                ? 'Incident'
                                : 'Monthly'
                            : ''

                          const totalAttachments =
                            attachmentSummary(r.attachedForm) +
                            attachmentSummary(r.checklistAttachments) +
                            attachmentSummary(r.findingAttachments) +
                            attachmentSummary(r.officeAttachments)

                          return (
                            <tr key={r.id}>
                              <td className='text-center'>{indexOfFirstRecord + idx + 1}</td>
                              <td className='text-nowrap'>{r.scmName || '-'}</td>
                              <td className='text-nowrap'>{r.dateHeld || '-'}</td>
                              <td className='text-nowrap'>
                                <div className='d-flex align-items-center gap-2'>
                                  <span>{r.findingName || '-'}</span>
                                  {meetingTypeLabel && (
                                    <span
                                      className={`badge ${
                                        r.findingsLinkType === 'AUDIT'
                                          ? 'badge-light-info'
                                          : r.findingsLinkType === 'INSPECTION'
                                            ? 'badge-light-primary'
                                            : 'badge-light-warning'
                                      }`}
                                    >
                                      {meetingTypeLabel}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <RemarksCell
                                  text={r.mastersRemarks}
                                  title="Master's Remarks"
                                  subtitle={`${r.scmName || `SCM #${r.id}`} • ${r.dateHeld || ''}`}
                                  onOpenModal={openRemarksModal}
                                />
                              </td>
                              <td>
                                <RemarksCell
                                  text={r.officeRemarks}
                                  title="Office Remarks"
                                  subtitle={`${r.scmName || `SCM #${r.id}`} • ${r.dateHeld || ''}`}
                                  onOpenModal={openRemarksModal}
                                />
                              </td>
                              <td>
                                {totalAttachments > 0 ? (
                                  <button
                                    type='button'
                                    className='btn btn-light-primary btn-sm d-inline-flex align-items-center text-nowrap'
                                    onClick={() => openAllAttachmentsModal(r)}
                                    title='View attachments'
                                    disabled={!canReadScm}
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/files/fil003.svg'
                                      className='svg-icon-3 me-1'
                                    />
                                    View Files ({totalAttachments})
                                  </button>
                                ) : (
                                  <span className='text-muted'>-</span>
                                )}
                              </td>
                              <td className='text-nowrap'>
                                {(() => {
                                  const statusMeta = getStatusDisplay(r, shipboardUser)
                                  return (
                                    <span className={`badge ${statusMeta.badgeClass}`}>
                                      {statusMeta.label}
                                    </span>
                                  )
                                })()}
                              </td>
                              <td className='text-nowrap'>
                                <div className='d-flex justify-content-left gap-1'>
                                  {/* View */}
                                  <button
                                    className='btn btn-icon btn-sm'
                                    title='View'
                                    disabled={!canReadScm}
                                    onClick={() => openView(r.id)}
                                  >
                                    <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                  </button>

                                  {/* Edit */}
                                  {showScmEdit && (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='Edit'
                                      onClick={() => openEdit(r)}
                                    >
                                      <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                    </button>
                                  )}

                                  {/* Submit */}
                                  {showScmSubmit && (
                                    <button
                                      type='button'
                                      className='btn btn-light-primary btn-sm'
                                      onClick={() => setSubmitId(r.id)}
                                    >
                                      Submit
                                    </button>
                                  )}

                                  {canReview && (
                                    <button
                                      type='button'
                                      className='btn btn-light-warning btn-sm'
                                      onClick={() => setReviewing(r)}
                                    >
                                      Review
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                  style={{
                    position: 'static',
                    bottom: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                  }}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{fontSize: '14px'}}>
                      Showing{' '}
                      <strong>
                        {sortedRecords.length === 0
                          ? 0
                          : ((currentPage - 1) * rowsPerPage) + 1}
                        -
                        {Math.min(currentPage * rowsPerPage, sortedRecords.length)}
                      </strong>{' '}
                      of <strong>{sortedRecords.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          if (startPage > 1) {
                            pages.push(
                              <li key={1} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(1)}
                                >
                                  1
                                </button>
                              </li>
                            )

                            if (startPage > 2) {
                              pages.push(
                                <li key='ellipsis1' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li
                                key={i}
                                className={`page-item ${currentPage === i ? 'active' : ''}`}
                              >
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxShadow: 'none',
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <li key='ellipsis2' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }

                            pages.push(
                              <li key={totalPages} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            )
                          }

                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SCMFormModal
        visible={addOpen}
        title='Add Safety Committee Meeting'
        shipboardUser={shipboardUser}
        canEditMastersRemarks={canEditScmRemarks}
        canEditOfficeRemarks={canEditScmRemarks}
        vessels={vesselsScoped}
        companies={companies}
        subcompanies={subcompanies}
        isTopLevel={isTopLevel}
        effectiveCompanyId={effectiveCompanyId}
        defaultVesselId={shipboardUser ? currentUser?.vessel?.id : undefined}
        onClose={() => setAddOpen(false)}
        onSave={handleAddSave}
        onSubmit={handleAddSubmit}
      />

      <SCMFormModal
        visible={editOpen}
        title='Edit Safety Committee Meeting'
        scmId={editing?.id}
        shipboardUser={shipboardUser}
        canEditMastersRemarks={canEditScmRemarks}
        canEditOfficeRemarks={canEditScmRemarks}
        vessels={vesselsScoped}
        companies={companies}
        subcompanies={subcompanies}
        isTopLevel={isTopLevel}
        effectiveCompanyId={effectiveCompanyId}
        defaultVesselId={editing?.vesselId ?? (shipboardUser ? currentUser?.vessel?.id : undefined)}
        initial={editing ? ({
          vesselId: editing.vesselId ?? null,
          scmName: editing.scmName || '',
          dateHeld: editing.dateHeld,
          findingName: editing.findingName,
          findingsLinkType: editing.findingsLinkType,
          findingId: editing.findingId ?? null,
          presentSafetyOfficer: editing.presentSafetyOfficer,
          designatedSafetyOfficer: editing.designatedSafetyOfficer,
          mastersRemarks: editing.mastersRemarks,
          officeRemarks: editing.officeRemarks,
          attachedForm: editing.attachedForm,
          checklistAttachments: editing.checklistAttachments,
          findingAttachments: editing.findingAttachments,
          officeAttachments: editing.officeAttachments,
        } as any) : undefined}
        onClose={() => {
          setEditOpen(false)
          setEditing(null)
        }}
        onSave={handleEditSave}
        onSubmit={handleEditSubmit}
      />

      {/* Submit confirmation modal */}
      {submitId !== null && (
        <div
          style={{
            background: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          tabIndex={-1}
        >
          <div
            className='bg-white text-dark rounded shadow-lg d-flex flex-column'
            style={{
              width: '40vw',
              maxWidth: '500px',
            }}
          >
            <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
              <h5 className='modal-title text-dark m-0'>Submit Safety Committee Meeting</h5>
              <button type='button' className='btn-close m-0' onClick={() => setSubmitId(null)} />
            </div>
            <div className='px-5 py-4'>
              <p className='mb-0'>Once submitted, this meeting cannot be edited by the crew.</p>
            </div>
            <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
              <button type='button' className='btn btn-light btn-sm' onClick={() => setSubmitId(null)}>
                Cancel
              </button>
              <button
                type='button'
                className='btn btn_primary btn-sm'
                onClick={() => {
                  if (submitId != null) handleSubmitRow(submitId)
                  setSubmitId(null)
                }}
              >
                Submit &amp; Close
              </button>
            </div>
          </div>
        </div>
      )}

      <SCMReviewModal
        visible={!!reviewing}
        record={reviewing}
        shipboardUser={shipboardUser}
        onClose={() => setReviewing(null)}
        onSave={handleSaveReview}
        onCloseWithResult={handleCloseWithResult}
      />

      {/* View SCM Modal */}
      <ViewSCMModal
        visible={viewOpen}
        record={viewing}
        onCancel={() => {
          setViewOpen(false)
          setViewing(null)
        }}
      />

      <SCMAttachmentsModal
        visible={attachmentsModal.visible}
        loading={attachmentsModal.loading}
        title={attachmentsModal.title}
        subtitle={attachmentsModal.subtitle}
        items={attachmentsModal.items}
        onClose={() => setAttachmentsModal((prev) => ({...prev, visible: false}))}
        onViewFile={openFileViewer}
      />

      <FileViewerModal
        visible={fileModal.visible}
        title={fileModal.title}
        fileName={fileModal.fileName}
        viewUrl={fileModal.viewUrl}
        downloadUrl={fileModal.downloadUrl}
        onClose={() => setFileModal((prev) => ({...prev, visible: false}))}
      />

      <SCMRemarksModal
        visible={remarksModal.visible}
        title={remarksModal.title}
        subtitle={remarksModal.subtitle}
        remarksText={remarksModal.text}
        onClose={() => setRemarksModal(prev => ({...prev, visible: false}))}
      />

      <ToastContainer position='top-center' />
    </div>
  )
}

export default SafetyCommitteeMeetingPage
